import { LabelWithDescription } from '@/components/LabelWithdescription';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteOutline } from '@mui/icons-material';
import { Button, Card, Flex, Form, Input, notification, Spin, Switch } from 'antd';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { OverViewCardForm } from '../OverviewCardForm';
import {
	QueryObserverResult,
	RefetchOptions,
	useMutation,
	UseMutationResult,
} from '@tanstack/react-query';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId, getUserFeatures } from '@/store/account/selector';
import { closeModal } from '@/store/modal/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { User } from '@/types/TAccounts';
import ImageUpload from './ImageUpload';
import { uploadToS3 } from '@/api/voice';
import ShortUniqueId from 'short-unique-id';
import { WorkflowType } from 'src/enums/EWorkflow';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { incrementScenarioCount } from '@/store/scenarioCounts/slice';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';

const uid = new ShortUniqueId({ length: 10 });

interface AddTextingWorkflowFormProps {
	initialValues?: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	debrand: boolean;
	isNoviUser: boolean;
	disableSubmittion?: boolean;
	isDuplicate?: boolean;
	showAddFieldButton?: boolean;
}

export interface AddTextingWorkflowFormRef {
	form: any;
}

const AddTextingWorkflowForm = forwardRef<AddTextingWorkflowFormRef, AddTextingWorkflowFormProps>(
	(
		{
			initialValues,
			refetch,
			mutation,
			debrand,
			isNoviUser,
			disableSubmittion,
			isDuplicate = false,
			showAddFieldButton = true,
		},
		ref,
	) => {
		const [form] = Form.useForm();
		const dispatch = useAppDispatch();
		const features = useAppSelector(getUserFeatures);
		const selectedAgentId = useAppSelector(getSelectedAgentId);
		const selectedAgent = useAppSelector(getSelectedAgent);
		const canAddScenario = useAppSelector(selectCanAddScenario);

		const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);
		const [imageFiles, setImageFiles] = useState<{
			[newScenarioIndex: number]: { file: File; s3Key?: string };
		}>({});

		const { mutate: uploadToS3Fn } = useMutation({
			mutationKey: ['uploadToS3'],
			mutationFn: uploadToS3,
			onError: (error: any) => {
				notification.error({
					message: error.message || 'An error occurred during the upload.',
				});
			},
		});

		useImperativeHandle(ref, () => ({ form }), [form]);

		useEffect(() => {
			form.resetFields();
			setImageFiles({});
			setIsFieldTouchReset(true);

			return () => {
				form.resetFields();
				setImageFiles({});
			};
		}, [form]);

		const uploadImageToS3 = async (file: File, scenarioIndex: number) => {
			const formData = new FormData();
			const now = new Date();
			const timestamp = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${now.getFullYear()}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
			const fileExtension = file?.name.substring(file?.name.lastIndexOf('.'));
			const s3Key = `${selectedAgent?.number}_${timestamp}${fileExtension}`;

			formData.append('file', file);
			formData.append('s3Key', s3Key);

			try {
				await uploadToS3Fn(formData);
				setImageFiles((prev) => ({
					...prev,
					[scenarioIndex]: { file, s3Key },
				}));
				return s3Key;
			} catch {
				notification.error({
					message: 'Image upload failed',
					description: 'Could not upload image to S3',
				});
				return null;
			}
		};

		const handleAddScenario = (add: () => void) => {
			if (!canAddScenario) {
				notification.error({
					message: 'Scenario limit reached',
					description: 'Max scenarios (100) reached. Please Delete existing to add more.',
				});
				return;
			}
			add();
		};

		const handleAddButtonClick = (add: () => void) => {
			if (features?.workflows?.textWroflows?.value.toString() === '-1') {
				handleAddScenario(() => {
					add();
				});
				return;
			}

			if (
				initialValues?.length ||
				parseInt(features?.workflows?.textWroflows?.value.toString() || '1') > 0
			) {
				handleAddScenario(() => {
					add();
				});
			} else {
				dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
			}
		};

		const handleSubmit = async (values: {
			workflows: {
				id?: string;
				description: string;
				voiceResponse: string;
				textMessage: URL | string;
				askForPermissionOnText: boolean;
				shortURLEnabled: boolean;
				customTextFooter: string;
				postExecutionPrompt?: string;
				availability?: TWorkflowAvailabilities;
				configEnabled?: boolean;
				fallbackMessage?: string;
			}[];
		}) => {
			try {
				if (features?.workflows?.textWroflows?.value.toString() !== '-1') {
					if (
						initialValues?.length ||
						parseInt(features?.workflows?.textWroflows?.value.toString() || '1') <= 0
					) {
						dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
						return;
					}
				}

				const existingWorkflows = initialValues?.workflows?.[0]?.previousWorkflowData || [];

				let newScenarios = await Promise.all(
					values.workflows.map(async (scenario, formIndex) => {
						const hasNewImage = !!imageFiles[formIndex]?.file;
						let imageS3Key = undefined;

						if (hasNewImage) {
							imageS3Key = await uploadImageToS3(imageFiles[formIndex].file, formIndex);
						}

						return {
							id: uid.rnd(),
							description: scenario.description,
							textMessage: scenario.textMessage,
							voiceResponse: scenario.voiceResponse || '',
							postExecutionPrompt: scenario.postExecutionPrompt,
							askForPermissionOnText: scenario.askForPermissionOnText,
							shortURLEnabled:
								scenario.shortURLEnabled === undefined ? true : scenario.shortURLEnabled,
							customTextFooter: scenario.customTextFooter || '',
							imageS3Key,
							configEnabled: scenario.configEnabled ?? false,
							fallbackMessage: scenario.fallbackMessage || '',
							availability: scenario.availability || null,
						};
					}),
				);

				// Clean up any undefined imageS3Key fields
				newScenarios = newScenarios.map((scenario) => {
					const cleanScenario = { ...scenario };
					if (!cleanScenario.imageS3Key) {
						delete cleanScenario.imageS3Key;
					}
					return cleanScenario;
				});

				// Combine existing workflows with new ones
				const allWorkflows = [...existingWorkflows, ...newScenarios];

				await mutation.mutateAsync({
					workflows: allWorkflows,
					agentId: selectedAgentId as string,
				});

				dispatch(incrementScenarioCount(WorkflowType.TEXT));

				await refetch();

				notification.success({
					key: 'form-success',
					message: isDuplicate
						? 'Texting workflow has been duplicated successfully!'
						: 'New workflow scenario added successfully!',
					duration: 3,
				});

				form.resetFields();
				setImageFiles({});
				setIsFieldTouchReset(false);
				dispatch(closeModal());

				await analyzeAndUpdateWorkflow(
					{ workflows: allWorkflows },
					WorkflowType.TEXT,
					dispatch,
					selectedAgentId as string,
				);
			} catch (err) {
				console.error('Error adding texting workflow:', err);
				notification.error({
					message: 'Something went wrong, please try again later!',
				});
			}
		};

		const onDeleteScenario = (remove: (name: number) => void, name: number, formIndex: number) => {
			remove(name);
			setImageFiles((prev) => {
				const newFiles = { ...prev };
				delete newFiles[formIndex];
				return newFiles;
			});
		};
		const handleConfirmDelete = (
			remove: (name: number) => void,
			name: number,
			formIndex: number,
		) => {
			ConfirmDeleteModal({
				itemName: 'Scenario',
				onConfirm: () => onDeleteScenario(remove, name, formIndex),
			});
		};

		const baseClass = 'texting-workflow';

		const FormItems = () => (
			<Form.List name="workflows" initialValue={initialValues?.workflows || [{}]}>
				{(fields, { add, remove }) => (
					<>
						{fields.map(({ key, name, ...restField }, formIndex) => {
							const existingWorkflowsCount =
								initialValues?.workflows?.[0]?.previousWorkflowData?.length || 0;
							const scenarioNumber = existingWorkflowsCount + formIndex + 1;

							return (
								<Flex key={key} className="mb-2" gap={8}>
									<Card
										className="flex-1"
										title={
											!isDuplicate ? (
												<div className="flex justify-between">
													<span className="font-bold text-base">{`Scenario ${scenarioNumber}`}</span>
													{fields.length > 1 && (
														<Button
															type="primary"
															danger
															icon={<DeleteOutline />}
															onClick={() => handleConfirmDelete(remove, name, formIndex)}
														>
															Delete
														</Button>
													)}
												</div>
											) : (
												<span className="font-bold text-base">Customize Your Duplicate</span>
											)
										}
									>
										<Form.Item {...restField} name={[name, 'id']} hidden>
											<Input />
										</Form.Item>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Enable Short URLs"
												description="Enable short URLs to keep track of the links and get analytics."
											/>
											<Form.Item
												className=" mt-2 pt-2 "
												{...restField}
												name={[name, 'shortURLEnabled']}
											>
												<Switch defaultChecked className=" mt-2 mb-4" />
											</Form.Item>
										</div>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Scenario Description"
												description="Describe the scenario in which you would like to send the caller a text message. The AI is intelligent enough to know when the scenario is triggered."
											/>
											<Form.Item
												className=" mt-2 pt-2 "
												{...restField}
												name={[name, 'description']}
												rules={[
													{
														validator: (_, value) => {
															if (!value || value.trim() === '') {
																return Promise.reject("Description can't be blank");
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<Input.TextArea
													placeholder="e.g. Send the caller a copy of the menu. Execute whenever caller asks for menu or prices."
													autoSize={{ minRows: 1, maxRows: 3 }}
													data-testid="textingworkflow-scenario-desc-input"
												/>
											</Form.Item>
										</div>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2 mb-2"
												label="Text Message"
												description="Specify the text you want your AI receptionist to send when the scenario is triggered."
											/>
											<Form.Item
												className="mt-2 pt-2"
												{...restField}
												name={[name, 'textMessage']}
												rules={[
													{
														validator: (_, value) => {
															if (!value || value.trim() === '') {
																return Promise.reject("Text Message Can't be blank");
															}
															return Promise.resolve();
														},
													},
													{
														max: 1000,
														message: 'Max 1000 characters allowed',
													},
												]}
											>
												<Input.TextArea
													className={`${baseClass}_input_text_area`}
													aria-multiline
													placeholder="e.g. Here is our menu: www.restaurant.com/menu"
													data-testid="textingworkflow-text-message-input"
												/>
											</Form.Item>
											<div className="text-blue-700 text-xs font-semibold mb-2">
												* Max 1000 characters allowed{' '}
											</div>
										</div>

										<div className="space-y-2">
											<div className="border-b bg-white rounded-md">
												<LabelWithDescription
													customClassName="mt-2 mb-2"
													label="What should the AI do next?"
													description="Specify instructions for the AI to follow after it executes this workflow."
												/>
												<Form.Item
													className="mt-2 pt-2"
													{...restField}
													name={[name, 'postExecutionPrompt']}
												>
													<Input.TextArea
														placeholder="e.g., Tell the caller you've sent them a text message, and then trigger the intake form defined earlier to collect their information."
														autoSize={{ maxRows: 2 }}
														data-testid="textingworkflow-text-post-execution-input"
													/>
												</Form.Item>
											</div>
										</div>

										{debrand || isNoviUser ? (
											<div>
												<LabelWithDescription
													customClassName="mt-2"
													label="Custom Text Footer"
													description="Add a custom text footer to your text message."
												/>
												<Form.Item
													className=" mt-2 pt-2 "
													{...restField}
													name={[name, 'customTextFooter']}
													rules={[
														{
															max: 100,
															message: 'Max 100 characters allowed',
														},
													]}
												>
													<Input.TextArea
														placeholder="e.g. Sent by AI Receptionist"
														autoSize={{ minRows: 1, maxRows: 2 }}
													/>
												</Form.Item>
												<div className="text-blue-700 text-xs font-semibold mb-2">
													* Max 100 characters allowed{' '}
												</div>
											</div>
										) : null}

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Ask before sending Text SMS"
												description="If checked in, above message will be sent when the caller confirms for the text sms"
											/>
											<Form.Item
												className="pt-2 "
												{...restField}
												name={[name, 'askForPermissionOnText']}
											>
												<Switch />
											</Form.Item>
										</div>

										<Form.Item label="Image Upload" name={[name, 'image']}>
											<ImageUpload
												key={`add-image-${formIndex}`}
												data-testid="textingworkflow-image-upload-input"
												value={imageFiles[formIndex]?.file}
												onChange={(imageFile: File | null) => {
													if (imageFile) {
														setImageFiles((prev) => ({
															...prev,
															[formIndex]: { file: imageFile },
														}));
													} else {
														setImageFiles((prev) => {
															const newFiles = { ...prev };
															delete newFiles[formIndex];
															return newFiles;
														});
													}
												}}
												// ADD mode: never show presigned URL (always start fresh)
												presignedUrl=""
											/>
										</Form.Item>
									</Card>
								</Flex>
							);
						})}

						{showAddFieldButton && (
							<Form.Item>
								<Button
									type="dashed"
									onClick={() => handleAddButtonClick(add)}
									block
									icon={<PlusOutlined />}
								>
									Add Another Scenario
								</Button>
							</Form.Item>
						)}
					</>
				)}
			</Form.List>
		);

		return (
			<Spin
				spinning={mutation.isPending}
				indicator={
					<LoadingOutlined
						style={{ fontSize: '48px' }}
						spin
						data-testid="textingworkflow-form-spinner"
					/>
				}
			>
				<OverViewCardForm
					form={form}
					initialValues={false}
					formItems={<FormItems />}
					handleSubmit={handleSubmit}
					isFieldTouchReset={isFieldTouchReset}
					disableSubmittion={disableSubmittion}
					hideUnsavedChangesModal={isDuplicate}
				/>
			</Spin>
		);
	},
);

AddTextingWorkflowForm.displayName = 'AddTextingWorkflowForm';

export default AddTextingWorkflowForm;
